/**
 * Accordion - 접기/펼치기 패널 컴포넌트
 *
 * @param items - 아코디언 항목 배열 (key, title, content, icon, badge, disabled)
 * @param multiple - true이면 여러 패널을 동시에 열 수 있음
 * @param variant - 'default' | 'bordered' | 'separated' 스타일 변형
 */
'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from './Accordion.module.css';

export interface AccordionItem {
  key: string;
  title: string;
  content: React.ReactNode;
  icon?: React.ReactNode;
  badge?: string;
  disabled?: boolean;
}

export interface AccordionProps {
  items: AccordionItem[];
  defaultOpenKeys?: string[];
  multiple?: boolean;
  variant?: 'default' | 'bordered' | 'separated';
  className?: string;
}

function AccordionPanel({
  item,
  isOpen,
  onToggle,
}: {
  item: AccordionItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<string>(isOpen ? 'none' : '0px');

  useEffect(() => {
    if (isOpen) {
      const el = contentRef.current;
      if (el) {
        setMaxHeight(`${el.scrollHeight}px`);
        const timer = setTimeout(() => setMaxHeight('none'), 250);
        return () => clearTimeout(timer);
      }
    } else {
      // collapse: set explicit height first, then 0
      const el = contentRef.current;
      if (el) {
        setMaxHeight(`${el.scrollHeight}px`);
        requestAnimationFrame(() => {
          setMaxHeight('0px');
        });
      }
    }
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (item.disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      className={`${styles.item} ${item.disabled ? styles.disabled : ''} ${isOpen ? styles.open : ''}`}
      data-key={item.key}
    >
      <div
        className={styles.header}
        role="button"
        tabIndex={item.disabled ? -1 : 0}
        aria-expanded={isOpen}
        onClick={item.disabled ? undefined : onToggle}
        onKeyDown={handleKeyDown}
      >
        <span className={styles.headerContent}>
          {item.icon && <span className={styles.headerIcon}>{item.icon}</span>}
          <span className={styles.title}>{item.title}</span>
          {item.badge && <span className={styles.badge}>{item.badge}</span>}
        </span>
        <ChevronDown
          size={16}
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
        />
      </div>
      <div
        ref={contentRef}
        className={styles.contentWrapper}
        style={{ maxHeight }}
        aria-hidden={!isOpen}
      >
        <div className={styles.content}>{item.content}</div>
      </div>
    </div>
  );
}

export default function Accordion({
  items,
  defaultOpenKeys = [],
  multiple = false,
  variant = 'default',
  className = '',
}: AccordionProps) {
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set(defaultOpenKeys));

  const headerRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const handleToggle = useCallback(
    (key: string) => {
      setOpenKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          if (!multiple) {
            next.clear();
          }
          next.add(key);
        }
        return next;
      });
    },
    [multiple],
  );

  const handleContainerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const enabledItems = items.filter((i) => !i.disabled);
      const target = e.target as HTMLElement;
      const currentKey = target.closest(`.${styles.item}`)?.getAttribute('data-key');
      if (!currentKey) return;

      const currentIndex = enabledItems.findIndex((i) => i.key === currentKey);
      if (currentIndex === -1) return;

      let nextIndex = -1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % enabledItems.length;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (currentIndex - 1 + enabledItems.length) % enabledItems.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = enabledItems.length - 1;
      }

      if (nextIndex >= 0) {
        const nextKey = enabledItems[nextIndex].key;
        const nextHeader = headerRefs.current.get(nextKey);
        nextHeader?.focus();
      }
    },
    [items],
  );

  return (
    <div
      className={`${styles.accordion} ${styles[variant]} ${className}`}
      onKeyDown={handleContainerKeyDown}
    >
      {items.map((item) => (
        <div
          key={item.key}
          ref={(el) => {
            if (el) {
              const header = el.querySelector(`.${styles.header}`) as HTMLDivElement | null;
              if (header) headerRefs.current.set(item.key, header);
            }
          }}
        >
          <AccordionPanel
            item={item}
            isOpen={openKeys.has(item.key)}
            onToggle={() => handleToggle(item.key)}
          />
        </div>
      ))}
    </div>
  );
}
